import React from 'react';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, Button, Divider, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import type { Biomarker, Client } from '@/types/client';
import { colors, formatMetric } from './analysis.shared';

type BiomarkerStatus = 'normal' | 'low' | 'borderline' | 'high';
type BiomarkerCategory = string;
const CHANGE_CALCULATION_TOOLTIP = 'Change % = ((Previous - Current) / Previous) × 100. ↘ means current is lower than previous; ↗ means current is higher than previous.';

interface BiomarkerTrendItem {
  id: string;
  name: string;
  subtitle: string;
  currentValue: number;
  previousValue: number | null;
  /** Exact string from API when previous value was sent as a string. */
  previousValueText?: string | null;
  previousUnit?: string;
  /** testedDate of the `previous` record itself, as returned by the API. */
  previousTestedDate?: string | null;
  changePercent?: number | null;
  changeDirection?: string | null;
  hasPreviousFromApi?: boolean;
  hasChangePercentFromApi?: boolean;
  unit: string;
  referenceRange: string;
  interpretation: string | null;
  rangeLowerBound: number | null;
  rangeUpperBound: number | null;
  status: BiomarkerStatus;
  category: BiomarkerCategory;
  trendPoints: number[];
  trendDates: string[];
}

type BiomarkerTrendSource = Biomarker & {
  previousValue?: number | null;
  previousValueText?: string | null;
  previousUnit?: string | null;
  previousTestedDate?: string | null;
  changePercent?: number | null;
  changeDirection?: string | null;
  hasPreviousFromApi?: boolean;
  hasChangePercentFromApi?: boolean;
  trendValues?: number[];
  trendDates?: string[];
  panelCategory?: string | null;
};

interface BiomarkerTrendSectionProps {
  client?: Pick<Client, 'biomarkers'> | null;
  biomarkers?: Biomarker[] | null;
  hideExpandedDetails?: boolean;
  panelLabel?: string;
  panelDateLabel?: string;
  reportSelector?: React.ReactNode;
  onCategoryTabChange?: () => void;
}

function normalizeStatus(status: string | null | undefined): BiomarkerStatus {
  const normalized = String(status ?? '').trim().toLowerCase();
  if (normalized === 'high' || normalized === 'critical') return 'high';
  if (normalized === 'low') return 'low';
  if (normalized === 'borderline') return 'borderline';
  return 'normal';
}

function titleCase(text: string): string {
  return text
    .split(/[_\s-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function toCategoryLabel(category: string): string {
  if (!category) return 'General';
  return titleCase(category.replace(/[_-]+/g, ' ').trim());
}

function formatRangeNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(4)));
}

function parseReferenceRangeBounds(rangeText: string): { lower: number | null; upper: number | null } {
  const text = rangeText.trim();
  if (!text || text === '-') {
    return { lower: null, upper: null };
  }

  const numbers = (text.match(/\d[\d,]*(?:\.\d+)?/g) ?? [])
    .map((value) => Number(value.replace(/,/g, '')))
    .filter((value) => Number.isFinite(value));

  if (numbers.length >= 2) {
    for (let index = 0; index < numbers.length - 1; index += 1) {
      const lower = numbers[index];
      const upper = numbers[index + 1];
      if (lower < upper) {
        return { lower, upper };
      }
    }
  }

  const hasUpperOnlyHint = /(?:<=|<|less than|up to)/i.test(text);
  const hasLowerOnlyHint = /(?:>=|>|greater than|at least)/i.test(text);
  const singleValue = numbers[0];

  if (typeof singleValue === 'number' && Number.isFinite(singleValue)) {
    if (hasUpperOnlyHint) {
      return { lower: null, upper: singleValue };
    }
    if (hasLowerOnlyHint) {
      return { lower: singleValue, upper: null };
    }
  }

  return { lower: null, upper: null };
}

function formatExactBiomarkerValue(value: number): string {
  if (!Number.isFinite(value)) return '—';

  const raw = value.toString();
  if (raw.includes('e') || raw.includes('E')) {
    return new Intl.NumberFormat('en-US', {
      useGrouping: false,
      maximumSignificantDigits: 21,
    }).format(value);
  }

  const [integerPart, fractionPart] = raw.split('.');
  const formattedIntegerPart = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Number(integerPart));

  return fractionPart ? `${formattedIntegerPart}.${fractionPart}` : formattedIntegerPart;
}

function referenceRangeFromBiomarker(biomarker: Biomarker): string {
  const referenceRangeText = biomarker.referenceRangeText?.trim();
  if (referenceRangeText) return referenceRangeText;

  const hasLower = typeof biomarker.rangeLowerBound === 'number' && Number.isFinite(biomarker.rangeLowerBound);
  const hasUpper = typeof biomarker.rangeUpperBound === 'number' && Number.isFinite(biomarker.rangeUpperBound);
  const unitSuffix = biomarker.unit ? ` ${biomarker.unit}` : '';

  if (hasLower && hasUpper) {
    return `${formatRangeNumber(biomarker.rangeLowerBound as number)} - ${formatRangeNumber(biomarker.rangeUpperBound as number)}${unitSuffix}`;
  }
  if (hasLower) {
    return `>= ${formatRangeNumber(biomarker.rangeLowerBound as number)}${unitSuffix}`;
  }
  if (hasUpper) {
    return `<= ${formatRangeNumber(biomarker.rangeUpperBound as number)}${unitSuffix}`;
  }

  return '-';
}

function buildBiomarkerRows(biomarkers: Biomarker[] | null | undefined): BiomarkerTrendItem[] {
  const existing = biomarkers ?? [];
  if (!existing.length) return [];

  return existing.map((biomarker) => {
    const source = biomarker as BiomarkerTrendSource;
    const label = biomarker.biomarkerName?.trim() || titleCase(biomarker.biomarkerId || 'Biomarker');
    const status = normalizeStatus(biomarker.status);
    const previousValue = typeof source.previousValue === 'number' && Number.isFinite(source.previousValue)
      ? source.previousValue
      : null;
    const previousValueText = typeof source.previousValueText === 'string' && source.previousValueText.trim()
      ? source.previousValueText.trim()
      : null;
    const changePercent = typeof source.changePercent === 'number' && Number.isFinite(source.changePercent)
      ? source.changePercent
      : null;
    const hasPreviousFromApi = source.hasPreviousFromApi === true
      && (previousValue !== null || previousValueText !== null);
    const hasChangePercentFromApi = source.hasChangePercentFromApi === true && changePercent !== null;
    const trendPoints = Array.isArray(source.trendValues)
      ? source.trendValues.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
      : [];
    const sourceTrendDates = Array.isArray(source.trendDates)
      ? source.trendDates.filter((d): d is string => typeof d === 'string' && Boolean(d))
      : [];
    const resolvedTrendPoints = trendPoints.length > 0 ? trendPoints : [biomarker.value];
    const resolvedTrendDates = sourceTrendDates.length === resolvedTrendPoints.length
      ? sourceTrendDates
      : sourceTrendDates.slice(0, resolvedTrendPoints.length);

    const referenceRange = referenceRangeFromBiomarker(biomarker);
    const parsedRangeBounds = parseReferenceRangeBounds(referenceRange);
    const rangeLowerBound = typeof biomarker.rangeLowerBound === 'number' && Number.isFinite(biomarker.rangeLowerBound)
      ? biomarker.rangeLowerBound
      : parsedRangeBounds.lower;
    const rangeUpperBound = typeof biomarker.rangeUpperBound === 'number' && Number.isFinite(biomarker.rangeUpperBound)
      ? biomarker.rangeUpperBound
      : parsedRangeBounds.upper;

    return {
      id: biomarker.id,
      name: label,
      subtitle: 'Clinical Marker',
      currentValue: biomarker.value,
      previousValue,
      previousValueText,
      previousUnit: source.previousUnit || biomarker.unit || '',
      previousTestedDate: source.previousTestedDate ?? null,
      changePercent,
      changeDirection: source.changeDirection || null,
      hasPreviousFromApi,
      hasChangePercentFromApi,
      unit: biomarker.unit || '',
      referenceRange,
      interpretation: biomarker.interpretation ?? null,
      rangeLowerBound,
      rangeUpperBound,
      status,
      category: source.panelCategory?.trim() || 'other',
      trendPoints: resolvedTrendPoints,
      trendDates: resolvedTrendDates,
    };
  });
}

function statusStyles(status: BiomarkerStatus): { text: string; bg: string; dot: string; fg: string } {
  if (status === 'high') {
    return { text: 'High', bg: '#FEE4E2', dot: '#F04438', fg: '#F04438' };
  }
  if (status === 'low') {
    return { text: 'Low', bg: '#FEF0DB', dot: '#F59E0B', fg: '#D97706' };
  }
  if (status === 'borderline') {
    return { text: 'Borderline', bg: '#EFF8FF', dot: '#2E90FA', fg: '#175CD3' };
  }
  return { text: 'Normal', bg: '#E8F7EF', dot: '#2FB9B3', fg: '#2FB9B3' };
}

function metricColor(status: BiomarkerStatus): string {
  if (status === 'high') return '#F04438';
  if (status === 'low') return '#F59E0B';
  if (status === 'borderline') return '#2E90FA';
  return '#16A34A';
}

function ReferenceRangeBar({ lower, upper, value }: { lower: number | null; upper: number | null; value: number }) {
  if (lower == null && upper == null) return null;

  const fmt = (v: number) => formatRangeNumber(Math.round(v * 100) / 100);

  type Seg = { color: string; flex: number; min: number; max: number };
  const segments: Seg[] = [];
  const legend: { color: string; label: string }[] = [];

  if (lower != null && upper != null) {
    const rangeSpan = upper - lower;
    const ext = rangeSpan * 0.2;
    segments.push({ color: '#F59E0B', flex: 12, min: lower - ext, max: lower });
    segments.push({ color: '#12B76A', flex: 76, min: lower, max: upper });
    segments.push({ color: '#F04438', flex: 12, min: upper, max: upper + ext });
    legend.push({ color: '#F59E0B', label: `Low <${fmt(lower)}` });
    legend.push({ color: '#12B76A', label: `Normal ${fmt(lower)}–${fmt(upper)}` });
    legend.push({ color: '#F04438', label: `High >${fmt(upper)}` });
  } else if (upper != null) {
    const ext = upper * 0.2;
    segments.push({ color: '#F59E0B', flex: 12, min: 0, max: upper * 0.8 });
    segments.push({ color: '#12B76A', flex: 68, min: upper * 0.8, max: upper });
    segments.push({ color: '#F04438', flex: 20, min: upper, max: upper + ext });
    legend.push({ color: '#F59E0B', label: 'Low' });
    legend.push({ color: '#12B76A', label: `Normal <${fmt(upper)}` });
    legend.push({ color: '#F04438', label: `High >${fmt(upper)}` });
  } else if (lower != null) {
    const ext = lower * 0.2;
    segments.push({ color: '#F59E0B', flex: 20, min: lower - ext, max: lower });
    segments.push({ color: '#12B76A', flex: 68, min: lower, max: lower * 2 });
    segments.push({ color: '#F04438', flex: 12, min: lower * 2, max: lower * 3 });
    legend.push({ color: '#F59E0B', label: `Low <${fmt(lower)}` });
    legend.push({ color: '#12B76A', label: `Normal >${fmt(lower)}` });
    legend.push({ color: '#F04438', label: `High` });
  }

  // Compute marker position as a percentage across the flex-based bar
  const totalFlex = segments.reduce((sum, s) => sum + s.flex, 0);
  let markerPct: number | null = null;
  if (segments.length > 0) {
    if (value <= segments[0].min) {
      markerPct = 0;
    } else if (value >= segments[segments.length - 1].max) {
      markerPct = 100;
    } else {
      let acc = 0;
      for (const seg of segments) {
        if (value >= seg.min && value <= seg.max) {
          const frac = seg.max > seg.min ? (value - seg.min) / (seg.max - seg.min) : 0.5;
          markerPct = ((acc + frac * seg.flex) / totalFlex) * 100;
          break;
        }
        acc += seg.flex;
      }
    }
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ position: 'relative', display: 'flex', height: 10, borderRadius: '5px', overflow: 'visible', gap: '2px' }}>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', borderRadius: '5px', overflow: 'hidden', gap: '2px' }}>
          {segments.map((seg, i) => (
            <Box key={i} sx={{ flex: seg.flex, bgcolor: seg.color }} />
          ))}
        </Box>
        {markerPct != null && (
          <Box
            sx={{
              position: 'absolute',
              left: `${markerPct}%`,
              top: -3,
              bottom: -3,
              width: '2.5px',
              bgcolor: '#1D2939',
              borderRadius: '2px',
              transform: 'translateX(-50%)',
              zIndex: 1,
            }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
        {legend.map((item, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#667085', whiteSpace: 'nowrap' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function formatDisplayValue(value: number, unit: string): string {
  return `${formatExactBiomarkerValue(value)}${unit ? ` ${unit}` : ''}`;
}

function buildClinicalInterpretation(row: BiomarkerTrendItem, deltaPercent: number | null): string {
  if (row.interpretation && row.interpretation.trim()) {
    return row.interpretation;
  }
  const valueLabel = formatDisplayValue(row.currentValue, row.unit);
  const statusMessage = row.status === 'normal'
    ? 'within the expected clinical range'
    : row.status === 'high'
      ? 'significantly outside the expected clinical range'
      : 'outside the optimal clinical range';
  const validTrendPoints = row.trendPoints.filter((point) => Number.isFinite(point));
  const latestTrendValue = validTrendPoints[validTrendPoints.length - 1];
  const previousTrendValue = validTrendPoints[validTrendPoints.length - 2];
  const hasTrendDirection =
    validTrendPoints.length > 1
    && typeof latestTrendValue === 'number'
    && typeof previousTrendValue === 'number';
  const derivedTrendDirection = !hasTrendDirection
    ? null
    : Math.abs(latestTrendValue - previousTrendValue) < 0.0001
      ? 'stable'
      : latestTrendValue > previousTrendValue
        ? 'up'
        : 'down';
  const trendMessage = derivedTrendDirection === 'up'
    ? 'showing an upward trend'
    : derivedTrendDirection === 'down'
      ? 'showing a downward trend'
      : derivedTrendDirection === 'stable'
        ? 'remaining stable'
        : deltaPercent == null
          ? 'with trend unavailable for this report selection'
          : deltaPercent <= 0
            ? 'showing a positive reduction trend'
            : 'showing an upward trend';

  return `${row.name} of ${valueLabel} is ${statusMessage} against target ${row.referenceRange}, ${trendMessage} across recent reports.`;
}

function formatPanelDate(value: string | null | undefined): string {
  if (!value) return 'Unknown date';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 'Unknown date';
  return new Date(parsed).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const Sparkline: React.FC<{ points: number[]; color: string }> = ({ points, color }) => {
  const width = 100;
  const height = 32;
  const pad = 3;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(0.001, max - min);

  const coordinates = points.map((value, index) => {
    const x = pad + (index / Math.max(1, points.length - 1)) * (width - pad * 2);
    const y = pad + ((max - value) / span) * (height - pad * 2);
    return { x, y };
  });

  const linePath = coordinates.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`).join(' ');
  const areaPath = `${linePath} L ${coordinates[coordinates.length - 1].x.toFixed(2)} ${(height - pad).toFixed(2)} L ${coordinates[0].x.toFixed(2)} ${(height - pad).toFixed(2)} Z`;

  return (
    <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: '100%', maxWidth: 100, height: 32, display: 'block' }} aria-hidden>
      <path d={areaPath} fill={color} fillOpacity={0.12} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" />
    </Box>
  );
};

function niceYTicks(dataMin: number, dataMax: number, targetCount = 4): { ticks: number[]; step: number } {
  const range = dataMax - dataMin;
  if (range < 0.0001) {
    const v = dataMin;
    const step = Math.max(0.5, Math.abs(v) * 0.1) || 1;
    return { ticks: [+(v - step).toPrecision(10), +v.toPrecision(10), +(v + step).toPrecision(10)], step };
  }
  const roughStep = range / (targetCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const norm = roughStep / magnitude;
  const niceStep = norm <= 1 ? magnitude : norm <= 2 ? 2 * magnitude : norm <= 5 ? 5 * magnitude : 10 * magnitude;
  const tickMin = Math.floor(dataMin / niceStep) * niceStep;
  const tickMax = Math.ceil(dataMax / niceStep) * niceStep;
  const ticks: number[] = [];
  for (let t = tickMin; t <= tickMax + niceStep * 0.001; t = Math.round((t + niceStep) * 1e10) / 1e10) {
    ticks.push(t);
  }
  return { ticks, step: niceStep };
}

function formatTrendLabel(isoDate: string): string {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

const DetailTrendChart: React.FC<{ points: number[]; dates?: string[]; color: string; unit?: string }> = ({ points, dates, color, unit }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const sliceFrom = points.length > 4 ? points.length - 4 : 0;
  const chartPoints = points.slice(sliceFrom);
  const chartDates = dates && dates.length === points.length ? dates.slice(sliceFrom) : undefined;
  const labels = chartDates
    ? chartDates.map(formatTrendLabel)
    : [];

  const width = 340;
  const height = 155;
  const padLeft = 34;
  const padRight = 12;
  const padTop = 16;
  const padBottom = 30;

  const rawMin = Math.min(...chartPoints);
  const rawMax = Math.max(...chartPoints);
  const { ticks: yTicks, step: yTickStep } = niceYTicks(rawMin, rawMax);
  const yMin = yTicks[0];
  const yMax = yTicks[yTicks.length - 1];
  const ySpan = Math.max(0.0001, yMax - yMin);
  const yTickDecimals = yTickStep < 1 ? Math.ceil(-Math.log10(yTickStep)) : 0;

  const timestamps = chartDates ? chartDates.map((d) => Date.parse(d)) : null;
  const tsMin = timestamps ? Math.min(...timestamps) : 0;
  const tsSpan = timestamps ? Math.max(1, Math.max(...timestamps) - tsMin) : 1;

  const xFor = (index: number) => {
    if (chartPoints.length <= 1) return padLeft + (width - padLeft - padRight) / 2;
    if (timestamps) {
      return padLeft + ((timestamps[index] - tsMin) / tsSpan) * (width - padLeft - padRight);
    }
    return padLeft + (index / (chartPoints.length - 1)) * (width - padLeft - padRight);
  };
  const yFor = (value: number) => (
    padTop + ((yMax - value) / ySpan) * (height - padTop - padBottom)
  );

  const coords = chartPoints.map((value, index) => ({ x: xFor(index), y: yFor(value), value }));
  const path = coords
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');

  const xTicks = chartPoints.map((_, index) => xFor(index));

  const tooltipPadX = 7;
  const tooltipHeight = 22;
  const tooltipFontSize = 12;

  return (
    <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: '100%', height: 160, display: 'block' }}>
      {yTicks.map((tick, index) => {
        const y = yFor(tick);
        return (
          <g key={`y-${index}`}>
            <line
              x1={padLeft}
              y1={y}
              x2={width - padRight}
              y2={y}
              stroke="#D8E5ED"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
            <text
              x={padLeft - 8}
              y={y + 4}
              textAnchor="end"
              fontSize="0.75rem"
              fill="#98A2B3"
              fontFamily="Inter, sans-serif"
            >
              {formatMetric(tick, yTickDecimals)}
            </text>
          </g>
        );
      })}

      {xTicks.map((x, index) => (
        <line
          key={`x-${index}`}
          x1={x}
          y1={padTop}
          x2={x}
          y2={height - padBottom}
          stroke="#E3EDF3"
          strokeDasharray="4 4"
          strokeWidth={1}
        />
      ))}

      <path d={path} fill="none" stroke={color} strokeWidth={3} strokeLinecap="round" />
      {coords.map((point, index) => (
        <g key={`p-${index}`}>
          <circle cx={point.x} cy={point.y} r={4.6} fill={color} />
          <circle
            cx={point.x}
            cy={point.y}
            r={12}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          />
        </g>
      ))}

      {hoveredIndex !== null && (() => {
        const pt = coords[hoveredIndex];
        const label = `${formatExactBiomarkerValue(pt.value)}${unit ? ` ${unit}` : ''}`;
        const approxTextWidth = label.length * (tooltipFontSize * 0.56);
        const tooltipWidth = approxTextWidth + tooltipPadX * 2;
        const tooltipAboveY = pt.y - tooltipHeight - 8;
        const tooltipY = tooltipAboveY < padTop ? pt.y + 12 : tooltipAboveY;
        const tooltipX = Math.min(
          Math.max(padLeft, pt.x - tooltipWidth / 2),
          width - padRight - tooltipWidth
        );
        return (
          <g style={{ pointerEvents: 'none' }}>
            <rect
              x={tooltipX}
              y={tooltipY}
              width={tooltipWidth}
              height={tooltipHeight}
              rx={5}
              ry={5}
              fill="#1d2939"
              opacity={0.9}
            />
            <text
              x={tooltipX + tooltipWidth / 2}
              y={tooltipY + tooltipHeight / 2 + tooltipFontSize * 0.36}
              textAnchor="middle"
              fontSize={tooltipFontSize}
              fill="#fff"
              fontFamily="Inter, sans-serif"
              fontWeight="600"
            >
              {label}
            </text>
          </g>
        );
      })()}

      {labels.map((label, index) => (
        <text
          key={`label-${index}`}
          x={xFor(index)}
          y={height - 8}
          textAnchor="middle"
          fontSize="0.75rem"
          fill="#667085"
          fontFamily="Inter, sans-serif"
        >
          {label}
        </text>
      ))}
    </Box>
  );
};

export const BiomarkerTrendSection: React.FC<BiomarkerTrendSectionProps> = ({
  client,
  biomarkers,
  hideExpandedDetails = false,
  reportSelector,
  onCategoryTabChange,
}) => {
  const [activeTab, setActiveTab] = React.useState('all');
  const [expandedRowId, setExpandedRowId] = React.useState<string | null>(null);
  const sourceBiomarkers = React.useMemo(
    () => biomarkers ?? client?.biomarkers ?? [],
    [biomarkers, client?.biomarkers]
  );
  const rows = React.useMemo(
    () => buildBiomarkerRows(sourceBiomarkers),
    [sourceBiomarkers]
  );
  const categoryTabs = React.useMemo(() => {
    const defaultOrder = ['diabetes', 'thyroid', 'lipids', 'vitamins', 'iron', 'inflammation'];
    const uniqueCategories = Array.from(
      new Set(
        rows
          .map((row) => row.category.trim().toLowerCase())
          .filter(Boolean)
      )
    );
    const ordered = [
      ...defaultOrder.filter((category) => uniqueCategories.includes(category)),
      ...uniqueCategories.filter((category) => !defaultOrder.includes(category)).sort((a, b) => a.localeCompare(b)),
    ];

    return [{ key: 'all', label: 'All' }, ...ordered.map((category) => ({ key: category, label: toCategoryLabel(category) }))];
  }, [rows]);

  React.useEffect(() => {
    if (!categoryTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab('all');
    }
  }, [activeTab, categoryTabs]);

  const counts = React.useMemo(
    () => ({
      normal: rows.filter((row) => row.status === 'normal').length,
      low: rows.filter((row) => row.status === 'low').length,
      borderline: rows.filter((row) => row.status === 'borderline').length,
      high: rows.filter((row) => row.status === 'high').length,
    }),
    [rows]
  );

  const filteredRows = React.useMemo(
    () => (activeTab === 'all' ? rows : rows.filter((row) => row.category.trim().toLowerCase() === activeTab)),
    [activeTab, rows]
  );
  const currentHeaderDate = React.useMemo(() => {
    const rowWithCurrentDate = filteredRows.find((row) => row.trendDates.length > 0);
    if (!rowWithCurrentDate) return null;
    return formatPanelDate(rowWithCurrentDate.trendDates[rowWithCurrentDate.trendDates.length - 1]);
  }, [filteredRows]);
  const previousHeaderDate = React.useMemo(() => {
    // Use the actual `previous.testedDate` the API returned for this biomarker/report
    // context, rather than guessing a position inside the trend sparkline — the two
    // are not the same thing (trendDates spans full history; previous is whichever
    // single record the current comparison was baselined against).
    const rowWithPreviousDate = filteredRows.find((row) => Boolean(row.previousTestedDate));
    if (!rowWithPreviousDate?.previousTestedDate) return null;
    return formatPanelDate(rowWithPreviousDate.previousTestedDate);
  }, [filteredRows]);
  const showComparativeColumns = React.useMemo(
    () => rows.some((row) => {
      const hasPreviousValue = row.hasPreviousFromApi === true
        && (
          (typeof row.previousValue === 'number' && Number.isFinite(row.previousValue))
          || Boolean(row.previousValueText?.trim())
        );
      const hasChange = row.hasChangePercentFromApi === true
        && typeof row.changePercent === 'number'
        && Number.isFinite(row.changePercent);
      const hasTrendHistory = Array.isArray(row.trendPoints) && row.trendPoints.length > 1;
      return hasPreviousValue || hasChange || hasTrendHistory;
    }),
    [rows]
  );
  const tableGridTemplateColumns = showComparativeColumns
    ? 'minmax(190px, 1.35fr) minmax(90px, .75fr) minmax(110px, .8fr) minmax(122px, .9fr) minmax(90px, .75fr) minmax(84px, .7fr) minmax(136px, 1fr)'
    : 'minmax(220px, 1.65fr) minmax(120px, .95fr) minmax(150px, 1fr) minmax(130px, .95fr)';
  const tableHeaderColumns = React.useMemo(
    () => (
      showComparativeColumns
        ? [
          { label: 'Biomarker', date: null },
          { label: 'Current', date: currentHeaderDate },
          { label: 'Ref Range', date: null },
          { label: 'Status', date: null },
          { label: 'Previous', date: previousHeaderDate },
          { label: 'Change', date: null },
          { label: 'Trend', date: null },
        ]
        : [
          { label: 'Biomarker', date: null },
          { label: 'Current', date: currentHeaderDate },
          { label: 'Ref Range', date: null },
          { label: 'Status', date: null },
        ]
    ),
    [currentHeaderDate, previousHeaderDate, showComparativeColumns]
  );

  React.useEffect(() => {
    if (!expandedRowId) return;
    if (!filteredRows.some((row) => row.id === expandedRowId)) {
      setExpandedRowId(null);
    }
  }, [expandedRowId, filteredRows]);

  return (
    <Paper
      elevation={0}
      sx={{
        border: 'none',
        bgcolor: 'transparent',
        p: 0,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          mt: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: 1,
        }}
      >
        {reportSelector ? <Box sx={{ flexShrink: 0, pl: 0.5 }}>{reportSelector}</Box> : null}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 1.2,
            minWidth: 0,
            pr: 1,
          }}
        >
          {[
            { label: 'Normal', value: counts.normal, color: '#2FB9B3' },
            { label: 'Low', value: counts.low, color: '#F59E0B' },
            { label: 'Borderline', value: counts.borderline, color: '#2E90FA' },
            { label: 'High', value: counts.high, color: '#F04438' },
          ].map((summary) => (
            <Box key={summary.label} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.35 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.5rem', fontWeight: 700, lineHeight: 1, color: '#1f2937' }}>
                {summary.value}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: summary.color }}>{summary.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Divider sx={{ mt: 2.5, mx: 0.5 }} />

      <Box
        sx={{
          mt: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.7,
        }}
      >
        {categoryTabs.map((tab) => {
          const selected = activeTab === tab.key;
          return (
            <Button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTab(tab.key);
                onCategoryTabChange?.();
              }}
              sx={{
                minHeight: 32,
                minWidth: 96,
                borderRadius: '999px',
                px: 1.5,
                py: 0.5,
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: selected ? colors.cyan : '#fff',
                color: selected ? '#fff' : '#667085',
                border: selected ? `1px solid ${colors.cyan}` : '1px solid #e4e7ec',
                '&:hover': {
                  bgcolor: selected ? '#1a9bcb' : '#f8fafc',
                  borderColor: selected ? colors.cyan : '#d0d5dd',
                },
              }}
            >
              {tab.label}
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          mt: 1.25,
          border: '1px solid #e7edf2',
          borderRadius: '14px',
          bgcolor: '#fff',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: showComparativeColumns ? 900 : 690, px: { xs: 1.5, sm: 2, md: 2.25 } }}>
          {filteredRows.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: tableGridTemplateColumns,
                gap: 1,
                alignItems: 'center',
                py: 1.05,
                borderBottom: '1px solid #edf2f7',
              }}
            >
              {tableHeaderColumns.map(({ label, date }) => (
                <Box
                  key={label}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    minWidth: 0,
                    width: '100%',
                    justifyContent: label === 'Trend' ? 'center' : 'flex-start',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: '#98A2B3',
                      whiteSpace: 'nowrap',
                      textAlign: label === 'Trend' ? 'center' : 'left',
                    }}
                  >
                    {label}
                  </Typography>
                  {date && (label === 'Current' || label === 'Previous') ? (
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: '#B0B9C6',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {date}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}

          {filteredRows.map((row) => {
            const status = statusStyles(row.status);
            const hasPreviousValue = row.hasPreviousFromApi === true
              && (
                (typeof row.previousValue === 'number' && Number.isFinite(row.previousValue))
                || Boolean(row.previousValueText?.trim())
              );
            const previousDisplay = row.previousValueText?.trim()
              ? row.previousValueText.trim()
              : (
                typeof row.previousValue === 'number' && Number.isFinite(row.previousValue)
                  ? formatExactBiomarkerValue(row.previousValue)
                  : '—'
              );
            const deltaPercent = row.hasChangePercentFromApi === true
              && typeof row.changePercent === 'number'
              && Number.isFinite(row.changePercent)
              ? row.changePercent
              : null;
            const hasTrendHistory = Array.isArray(row.trendPoints) && row.trendPoints.length > 1;
            const direction = (row.changeDirection || '').trim().toLowerCase();
            const improving = deltaPercent == null
              ? null
              : (
                direction.includes('down')
                || direction.includes('decrease')
                || direction.includes('lower')
                || (!direction && deltaPercent <= 0)
              );
            const roundedDeltaPercent = deltaPercent == null
              ? null
              : Math.round(Math.abs(deltaPercent) * 10) / 10;
            const showDeltaArrow = roundedDeltaPercent != null && roundedDeltaPercent > 0;
            const statusAccentColor = row.status === 'high'
              ? '#F04438'
              : row.status === 'low'
                ? '#F59E0B'
              : row.status === 'borderline'
                ? '#2E90FA'
                : '#22C55E';
            const deltaColor = deltaPercent == null ? '#98a2b3' : statusAccentColor;
            const sparklineColor = statusAccentColor;
            const isExpanded = expandedRowId === row.id;
            const rowCanExpand = showComparativeColumns && !hideExpandedDetails && hasTrendHistory;
            const interpretation = buildClinicalInterpretation(row, deltaPercent);
            const summaryTone = row.status === 'high'
              ? { border: '#FECACA', bg: '#FFF1F2', title: '#B42318' }
              : row.status === 'low'
                ? { border: '#F5D0A8', bg: '#FFF7ED', title: '#D97706' }
              : row.status === 'borderline'
                ? { border: '#B2DDFF', bg: '#EFF8FF', title: '#175CD3' }
                : { border: '#A7F3D0', bg: '#ECFDF3', title: '#027A48' };

            return (
              <Box key={row.id} sx={{ borderBottom: '1px solid #edf2f7' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: tableGridTemplateColumns,
                    gap: 1,
                    alignItems: 'center',
                    py: 1.1,
                    minWidth: 0,
                    cursor: rowCanExpand ? 'pointer' : 'default',
                    '&:hover': rowCanExpand ? {
                      bgcolor: '#F9FAFB',
                    } : undefined,
                  }}
                  role={rowCanExpand ? 'button' : undefined}
                  tabIndex={rowCanExpand ? 0 : -1}
                  aria-expanded={rowCanExpand ? isExpanded : undefined}
                  aria-label={rowCanExpand ? (isExpanded ? `Collapse ${row.name} details` : `Expand ${row.name} details`) : undefined}
                  onClick={rowCanExpand ? () => setExpandedRowId((prev) => (prev === row.id ? null : row.id)) : undefined}
                  onKeyDown={(event) => {
                    if (!rowCanExpand) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setExpandedRowId((prev) => (prev === row.id ? null : row.id));
                    }
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Tooltip title={row.name} enterDelay={0} enterNextDelay={0} arrow placement="top">
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                          color: '#1f2937',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.name}
                      </Typography>
                    </Tooltip>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3' }}>{row.subtitle}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 4px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.02rem', fontWeight: 700, color: metricColor(row.status), whiteSpace: 'nowrap' }}>
                      {formatExactBiomarkerValue(row.currentValue)}
                    </Typography>
                    {row.unit ? (
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 500, color: '#98a2b3', whiteSpace: 'nowrap' }}>
                        {row.unit}
                      </Typography>
                    ) : null}
                  </Box>

                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98a2b3', overflowWrap: 'anywhere' }}>
                    {row.referenceRange}
                  </Typography>

                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.45,
                      px: 1.05,
                      py: 0.45,
                      borderRadius: '999px',
                      bgcolor: status.bg,
                      width: 'fit-content',
                      maxWidth: '100%',
                    }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: status.dot, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: status.fg,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {status.text}
                    </Typography>
                  </Box>

                  {showComparativeColumns ? (
                    <>
                      {hasPreviousValue ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0 4px', minWidth: 0 }}>
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#667085', whiteSpace: 'nowrap' }}>
                            {previousDisplay}
                          </Typography>
                          {(row.previousUnit || row.unit) ? (
                            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98a2b3', whiteSpace: 'nowrap' }}>
                              {row.previousUnit || row.unit}
                            </Typography>
                          ) : null}
                        </Box>
                      ) : (
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#98a2b3' }}>—</Typography>
                      )}

                      <Tooltip title={CHANGE_CALCULATION_TOOLTIP} arrow>
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: deltaColor, whiteSpace: 'nowrap', width: 'fit-content' }}>
                          {deltaPercent == null ? (
                            <Box component="span" sx={{ color: '#98a2b3', fontWeight: 400 }}>—</Box>
                          ) : (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  width: '1.05em',
                                  textAlign: 'center',
                                  mr: 0.2,
                                  visibility: showDeltaArrow ? 'visible' : 'hidden',
                                }}
                              >
                                {improving ? '↘' : '↗'}
                              </Box>
                              <Box component="span">{formatMetric(roundedDeltaPercent ?? 0, 1)}%</Box>
                            </Box>
                          )}
                        </Typography>
                      </Tooltip>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: hasTrendHistory ? 'flex-start' : 'center', gap: 0.8, minWidth: 0 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {hasTrendHistory ? (
                            <Sparkline points={row.trendPoints} color={sparklineColor} />
                          ) : (
                            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#98a2b3', textAlign: 'center' }}>—</Typography>
                          )}
                        </Box>
                        {rowCanExpand ? (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedRowId((prev) => (prev === row.id ? null : row.id));
                            }}
                            aria-label={isExpanded ? `Collapse ${row.name} details` : `Expand ${row.name} details`}
                            sx={{
                              p: 0.25,
                              color: '#98a2b3',
                              flexShrink: 0,
                              '&:hover': { bgcolor: '#f2f4f7' },
                            }}
                          >
                            <KeyboardArrowDownRoundedIcon
                              sx={{
                                fontSize: 18,
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 180ms ease',
                              }}
                            />
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 18, height: 18, flexShrink: 0 }} />
                        )}
                      </Box>
                    </>
                  ) : null}
                </Box>

                {isExpanded && rowCanExpand && (
                  <Box
                    sx={{
                      pb: 1.2,
                      pt: 0.35,
                      mx: { xs: -1.5, sm: -2, md: -2.25 },
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: '14px',
                        bgcolor: '#EAF8FD',
                        p: 1.2,
                        display: 'grid',
                        gap: 1.15,
                        gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr 1fr' },
                      }}
                    >
                      {row.rangeLowerBound != null || row.rangeUpperBound != null || row.referenceRange !== '-' ? (
                        <Box
                          sx={{
                            borderRadius: '12px',
                            bgcolor: '#fff',
                            border: '1px solid #D7EAF2',
                            p: 2.8,
                          }}
                        >
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#344054' }}>
                            Reference Range
                          </Typography>
                          {row.rangeLowerBound != null || row.rangeUpperBound != null ? (
                            <ReferenceRangeBar lower={row.rangeLowerBound} upper={row.rangeUpperBound} value={row.currentValue} />
                          ) : (
                            <Typography sx={{ mt: 1.5, fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#667085' }}>
                              {row.referenceRange}
                            </Typography>
                          )}
                        </Box>
                      ) : null}

                      <Box
                        sx={{
                          borderRadius: '12px',
                          bgcolor: '#fff',
                          border: '1px solid #D7EAF2',
                          p: 2.8,
                        }}
                      >
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#344054' }}>
                          Trend (All Reports)
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <DetailTrendChart points={row.trendPoints} dates={row.trendDates} color={sparklineColor} unit={row.unit} />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: '12px',
                          bgcolor: '#fff',
                          border: '1px solid #D7EAF2',
                          p: 2.8,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
                          <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: '#20AEDC' }} />
                          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#20AEDC' }}>
                            Clinical Interpretation
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            mt: 0.8,
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.75rem',
                            lineHeight: 1.55,
                            color: '#344054',
                          }}
                        >
                          {interpretation}
                        </Typography>

                        <Box
                          sx={{
                            mt: 1.15,
                            borderRadius: '12px',
                            border: `1px solid ${summaryTone.border}`,
                            bgcolor: summaryTone.bg,
                            px: 1.15,
                            py: 0.95,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 700,
                              fontSize: '0.78rem',
                              color: summaryTone.title,
                            }}
                          >
                            {status.text} - {formatDisplayValue(row.currentValue, row.unit)}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.35,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.75rem',
                              color: '#667085',
                            }}
                          >
                            Optimal: {row.referenceRange}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}

          {!filteredRows.length && (
            <Typography sx={{ py: 2, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#98a2b3' }}>
              No biomarkers available for this filter.
            </Typography>
          )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
